/**
 * Define Conditions and Planks: address each selected friction question before the Bridge Card.
 * For each item: review current state, define future condition, set future answer (correction factor),
 * define blocking assumption, and define change action (plank) with resource and duration.
 * @feature BRIDGE_SESSION_FLOW - Step between Friction Priorities and Bridge Card
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Spin,
  message,
  Typography,
  Input,
  Select,
  Form,
  Tabs,
  DatePicker,
  Modal,
  Space,
} from 'antd';
import { UserAddOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useBridgeSession } from '../../contexts/BridgeSessionContext';
import {
  getBridgeCard,
  getBridgeSession,
  getQuestions,
  updateBridgeCardDetails,
  updateBridgeSession,
  listCompanyEmployees,
  createCompanyEmployee,
  type BridgeCardItemWithDetails,
  type BridgeCardItemDetailsInput,
  type QuestionWithAnswer,
} from '../../services/localBridgeSessionApi';
import {
  getProductDomainBridgeCard,
  getProductDomainSession,
  getProductDomainQuestions,
  updateProductDomainBridgeCardDetails,
} from '../../services/productDomainSessionApi';
import styles from './DefineConditionsAndPlanks.module.css';

const { Text } = Typography;
const { TextArea } = Input;

function getOptionLabel(question: QuestionWithAnswer | undefined, score: number | undefined): string {
  if (score == null || !question?.options?.length) return score != null ? `Score ${score}` : 'Not answered';
  const opt = question.options.find((o) => o.score === score);
  return opt?.label ?? `Score ${score}`;
}

interface DefineConditionsAndPlanksProps {
  /** When provided, used instead of navigateToStep (e.g. for Product Domain flow) */
  onComplete?: () => void;
  onBack?: () => void;
  /** Optional label for back button (default: "Back to Friction Priorities") */
  backLabel?: string;
}

export const DefineConditionsAndPlanks: React.FC<DefineConditionsAndPlanksProps> = ({
  onComplete,
  onBack,
  backLabel = 'Back to Friction Priorities',
}) => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { sessionType, navigateToStep } = useBridgeSession();
  const handleComplete = onComplete ?? (() => navigateToStep('bridge_card'));
  const handleBack = onBack ?? (() => navigateToStep('friction_priorities'));
  const [items, setItems] = useState<BridgeCardItemWithDetails[]>([]);
  const [questions, setQuestions] = useState<QuestionWithAnswer[]>([]);
  const [employees, setEmployees] = useState<Array<{ id: string; name: string }>>([]);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [addEmployeeOpen, setAddEmployeeOpen] = useState(false);
  const [addEmployeeName, setAddEmployeeName] = useState('');
  const [addEmployeeLoading, setAddEmployeeLoading] = useState(false);
  const [form] = Form.useForm();

  const refreshEmployees = useCallback(async (cid: string) => {
    const empList = await listCompanyEmployees(cid);
    setEmployees(empList.map((e) => ({ id: e.id, name: e.name })));
  }, []);

  const loadData = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const [cardList, questionsList, session] = sessionType === 'product'
        ? await Promise.all([
            getProductDomainBridgeCard(sessionId),
            getProductDomainQuestions(sessionId, 'current'),
            getProductDomainSession(sessionId),
          ])
        : await Promise.all([
            getBridgeCard(sessionId),
            getQuestions(sessionId, 'current'),
            getBridgeSession(sessionId),
          ]);
      setItems(cardList as BridgeCardItemWithDetails[]);
      setQuestions(questionsList as QuestionWithAnswer[]);
      const cid = session?.company_id ?? null;
      setCompanyId(cid);
      if (cid) {
        await refreshEmployees(cid);
      } else {
        setEmployees([]);
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId, sessionType, refreshEmployees]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddEmployee = async () => {
    if (!companyId || !addEmployeeName.trim()) return;
    setAddEmployeeLoading(true);
    try {
      const created = await createCompanyEmployee(companyId, addEmployeeName.trim());
      if (!created) {
        message.error('Failed to add employee');
        return;
      }
      message.success(`Added ${created.name}`);
      setAddEmployeeOpen(false);
      setAddEmployeeName('');
      await refreshEmployees(companyId);
      // Assign to the first plank that has no employee yet.
      const values = form.getFieldsValue(true) as Record<string, unknown>;
      for (const item of items) {
        const key = `item_${item.question_id}_resource_id`;
        if (!values[key]) {
          form.setFieldValue(key, created.id);
          break;
        }
      }
    } finally {
      setAddEmployeeLoading(false);
    }
  };

  useEffect(() => {
    if (items.length === 0) return;
    const values: Record<string, unknown> = {};
    items.forEach((item) => {
      const prefix = `item_${item.question_id}`;
      values[`${prefix}_future_condition`] = item.future_condition ?? undefined;
      values[`${prefix}_future_answer_score`] = item.future_answer_score ?? undefined;
      values[`${prefix}_assumption`] = item.assumption_text ?? undefined;
      values[`${prefix}_plank`] = item.plank_description ?? undefined;
      values[`${prefix}_resource`] = item.resource_assignment ?? undefined;
      values[`${prefix}_duration`] = item.estimated_duration ?? undefined;
      values[`${prefix}_resource_id`] = item.resource_id ?? undefined;
      values[`${prefix}_start_date`] = item.start_date ? dayjs(item.start_date) : undefined;
      values[`${prefix}_end_date`] = item.end_date ? dayjs(item.end_date) : undefined;
      values[`${prefix}_remaining_duration`] = item.remaining_duration ?? undefined;
    });
    form.setFieldsValue(values);
  }, [items, form]);

  const handleFinish = async (values: Record<string, unknown>) => {
    if (!sessionId || items.length === 0) return;
    setSubmitting(true);
    try {
      const payload: BridgeCardItemDetailsInput[] = items.map((item) => {
        const prefix = `item_${item.question_id}`;
        const startDate = values[`${prefix}_start_date`] as dayjs.Dayjs | undefined;
        const endDate = values[`${prefix}_end_date`] as dayjs.Dayjs | undefined;
        return {
          question_id: item.question_id,
          future_condition: values[`${prefix}_future_condition`] as string | undefined,
          future_answer_score: values[`${prefix}_future_answer_score`] as number | undefined,
          assumption_text: values[`${prefix}_assumption`] as string | undefined,
          plank_description: values[`${prefix}_plank`] as string | undefined,
          resource_assignment: values[`${prefix}_resource`] as string | undefined,
          estimated_duration: values[`${prefix}_duration`] as string | undefined,
          resource_id: values[`${prefix}_resource_id`] as string | undefined,
          start_date: startDate?.format('YYYY-MM-DD'),
          end_date: endDate?.format('YYYY-MM-DD'),
          duration: values[`${prefix}_duration`] as string | undefined,
          remaining_duration: values[`${prefix}_remaining_duration`] as string | undefined,
        };
      });
      const ok = sessionType === 'product'
        ? (await updateProductDomainBridgeCardDetails(sessionId, payload))?.ok ?? false
        : await updateBridgeCardDetails(sessionId, payload);
      if (ok) {
        if (sessionType !== 'product') {
          await updateBridgeSession(sessionId, { status: 'bridge_built' });
        }
        message.success('Conditions and planks saved. Opening Bridge Card.');
        handleComplete();
      } else {
        message.error('Failed to save.');
      }
    } catch {
      message.error('Failed to save.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinueToBridgeCard = () => {
    form.submit();
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={styles.wrapper}>
        <Card>
          <p>No bridge card items yet. Select friction priorities first.</p>
          <Button type="primary" onClick={handleBack}>
            {backLabel}
          </Button>
        </Card>
      </div>
    );
  }

  const sortedItems = [...items].sort((a, b) => a.priority - b.priority);

  const tabItems = sortedItems.map((item, index) => {
    const prefix = `item_${item.question_id}`;
    const question = questions.find((q) => q.question_id === item.question_id);
    const questionText = question?.text || item.reason || `Question ${item.question_id}`;
    const currentAnswerLabel = getOptionLabel(question, question?.current_answer?.score);
    const futureAnswerOptions = (question?.options ?? []).map((o) => ({
      value: o.score,
      label: o.label,
    }));
    return {
      key: item.question_id,
      label: `Priority ${index + 1}`,
      children: (
        <div className={styles.tabContent}>
          <div className={styles.currentState}>
            <Text strong>Current state (question)</Text>
            <div className={styles.questionText}>{questionText}</div>
            <div className={styles.currentAnswer}>
              <Text type="secondary">Current answer: </Text>
              <Text>{currentAnswerLabel}</Text>
            </div>
          </div>

          <Form.Item
            name={`${prefix}_future_condition`}
            label="Future condition"
            rules={[{ required: true, message: 'Define the desired future condition' }]}
          >
            <TextArea rows={2} placeholder="What should be true after this is addressed?" />
          </Form.Item>

          <Form.Item
            name={`${prefix}_future_answer_score`}
            label="Future answer"
            tooltip="Target answer for this question; drives correction factor."
            rules={[{ required: true, message: 'Select target answer' }]}
          >
            <Select
              placeholder="Select target answer"
              options={futureAnswerOptions}
              allowClear
            />
          </Form.Item>

          <Form.Item
            name={`${prefix}_assumption`}
            label="Current assumption blocking this condition"
            rules={[{ required: true, message: 'State the blocking assumption' }]}
          >
            <TextArea rows={2} placeholder="Why isn't this condition true today?" />
          </Form.Item>

          <Form.Item
            name={`${prefix}_plank`}
            label="Change action (plank)"
            rules={[{ required: true, message: 'Define the change action' }]}
          >
            <TextArea rows={2} placeholder="What change or action will bridge to the future condition?" />
          </Form.Item>

          <Form.Item name={`${prefix}_resource`} label="Resource assignment (free text)">
            <Input placeholder="Who or what will do this? (e.g. team, role, tool)" />
          </Form.Item>

          <Form.Item
            name={`${prefix}_resource_id`}
            label="Assigned employee"
            extra={
              companyId ? (
                <Button
                  type="link"
                  size="small"
                  icon={<UserAddOutlined />}
                  onClick={() => setAddEmployeeOpen(true)}
                  style={{ padding: 0 }}
                >
                  Add employee
                </Button>
              ) : (
                <Text type="secondary">Company not linked to this session; cannot load employees.</Text>
              )
            }
          >
            <Select
              placeholder={employees.length === 0 ? 'No employees yet — add one' : 'Select employee'}
              allowClear
              options={employees.map((e) => ({ value: e.id, label: e.name }))}
              notFoundContent={
                <Space direction="vertical" size={4} style={{ padding: 8 }}>
                  <Text type="secondary">No employees for this company.</Text>
                  {companyId && (
                    <Button
                      type="link"
                      size="small"
                      icon={<UserAddOutlined />}
                      onClick={() => setAddEmployeeOpen(true)}
                    >
                      Add employee
                    </Button>
                  )}
                </Space>
              }
            />
          </Form.Item>

          <Form.Item name={`${prefix}_start_date`} label="Start date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name={`${prefix}_end_date`} label="End date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name={`${prefix}_duration`} label="Estimated duration">
            <Input placeholder="e.g. 2 weeks, 1 day, 3 months" />
          </Form.Item>

          <Form.Item name={`${prefix}_remaining_duration`} label="Remaining duration">
            <Input placeholder="e.g. 1 week remaining" />
          </Form.Item>
        </div>
      ),
    };
  });

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <Typography.Title level={2}>Define conditions and planks</Typography.Title>
        <Text type="secondary">
          For each high-friction question: review current state, define the future condition and future answer,
          state the assumption blocking it, and define the change action with resource and duration.
        </Text>
      </div>

      <Form form={form} layout="vertical" onFinish={handleFinish} preserve>
        <Tabs
          defaultActiveKey={sortedItems[0]?.question_id}
          items={tabItems}
          className={styles.tabs}
          destroyInactiveTabPane={false}
        />

        <div className={styles.actions}>
          <Button
            type="primary"
            size="large"
            onClick={handleContinueToBridgeCard}
            loading={submitting}
          >
            Apply to Bridge Card
          </Button>
          <Button onClick={handleBack}>
            {backLabel}
          </Button>
        </div>
      </Form>

      <Modal
        title="Add employee"
        open={addEmployeeOpen}
        onCancel={() => {
          setAddEmployeeOpen(false);
          setAddEmployeeName('');
        }}
        onOk={() => void handleAddEmployee()}
        confirmLoading={addEmployeeLoading}
        okText="Add"
        okButtonProps={{ disabled: !addEmployeeName.trim() }}
        destroyOnClose
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Creates a company employee available for plank assignment on this and future sessions.
        </Text>
        <Input
          autoFocus
          placeholder="Employee name"
          value={addEmployeeName}
          onChange={(e) => setAddEmployeeName(e.target.value)}
          onPressEnter={() => void handleAddEmployee()}
        />
      </Modal>
    </div>
  );
};

export default DefineConditionsAndPlanks;
